import { ReactNode, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarDaysIcon,
  ChatBubbleLeftIcon,
  CheckIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  QueueListIcon,
  Squares2X2Icon,
  TableCellsIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { DraggableDetailPanel } from '../../components/ui/DraggableDetailPanel';
import {
  PortalActivitiesData,
  PortalActivityItem,
  PortalActivityPriority,
  PortalActivityStatus,
} from './portalActivitiesMock';

type ActivityView = 'board' | 'list' | 'backlog';
type DetailTab = 'details' | 'comments' | 'files';

const viewOptions: { id: ActivityView; label: string; icon: typeof Squares2X2Icon }[] = [
  { id: 'board', label: 'Quadro', icon: Squares2X2Icon },
  { id: 'list', label: 'Lista', icon: TableCellsIcon },
  { id: 'backlog', label: 'Backlog', icon: QueueListIcon },
];

const boardColumns: {
  id: PortalActivityStatus;
  label: string;
  headerClass: string;
  badgeClass: string;
}[] = [
  { id: 'todo', label: 'A fazer', headerClass: 'bg-sky-50 text-sky-800', badgeClass: 'bg-sky-100 text-sky-800' },
  {
    id: 'inProgress',
    label: 'Em andamento',
    headerClass: 'bg-amber-50 text-amber-800',
    badgeClass: 'bg-amber-100 text-amber-800',
  },
  {
    id: 'review',
    label: 'Em revisão',
    headerClass: 'bg-violet-50 text-violet-800',
    badgeClass: 'bg-violet-100 text-violet-800',
  },
  {
    id: 'done',
    label: 'Concluído',
    headerClass: 'bg-emerald-50 text-emerald-800',
    badgeClass: 'bg-emerald-100 text-emerald-800',
  },
];

const statusLabel: Record<PortalActivityStatus, string> = {
  todo: 'A fazer',
  inProgress: 'Em andamento',
  review: 'Em revisão',
  done: 'Concluído',
};

const statusDotClass: Record<PortalActivityStatus, string> = {
  todo: 'bg-sky-500',
  inProgress: 'bg-amber-500',
  review: 'bg-violet-500',
  done: 'bg-emerald-500',
};

const priorityLabel: Record<PortalActivityPriority, string> = {
  high: 'Alta',
  medium: 'Média',
  low: 'Baixa',
};

const priorityClass: Record<PortalActivityPriority, string> = {
  high: 'bg-red-50 text-red-700',
  medium: 'bg-secondary-100 text-secondary-700',
  low: 'bg-emerald-50 text-emerald-700',
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

type PortalProjectActivitiesProps = {
  activities: PortalActivitiesData;
  projectPath: string;
};

export function PortalProjectActivities({ activities, projectPath }: PortalProjectActivitiesProps) {
  const [view, setView] = useState<ActivityView>('board');
  const [selectedId, setSelectedId] = useState<string | null>('act-modules');
  const [detailTab, setDetailTab] = useState<DetailTab>('details');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | PortalActivityStatus>('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState<'all' | PortalActivityPriority>('all');

  const owners = useMemo(
    () => Array.from(new Set(activities.items.map((item) => item.ownerName))).sort(),
    [activities.items],
  );

  const filtered = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('pt-BR');
    return activities.items.filter((item) => {
      if (normalized) {
        const haystack = `${item.reference} ${item.title} ${item.description}`.toLocaleLowerCase('pt-BR');
        if (!haystack.includes(normalized)) return false;
      }
      if (statusFilter !== 'all' && item.status !== statusFilter) return false;
      if (ownerFilter !== 'all' && item.ownerName !== ownerFilter) return false;
      if (stageFilter !== 'all' && item.stageId !== stageFilter) return false;
      if (priorityFilter !== 'all' && item.priority !== priorityFilter) return false;
      return true;
    });
  }, [activities.items, ownerFilter, priorityFilter, query, stageFilter, statusFilter]);

  const selected = filtered.find((item) => item.id === selectedId) ?? activities.items.find((item) => item.id === selectedId) ?? null;

  function clearFilters() {
    setQuery('');
    setStatusFilter('all');
    setOwnerFilter('all');
    setStageFilter('all');
    setPriorityFilter('all');
  }

  const hasFilters =
    query.trim() !== '' ||
    statusFilter !== 'all' ||
    ownerFilter !== 'all' ||
    stageFilter !== 'all' ||
    priorityFilter !== 'all';

  return (
    <div className={`relative space-y-5 ${selected ? 'xl:pr-[24rem]' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-secondary-900">Atividades do projeto</h2>
          <p className="mt-1 max-w-2xl text-sm text-secondary-600">
            Acompanhe o andamento das atividades compartilhadas, prioridades e responsáveis.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-[12rem] rounded-xl border border-secondary-200 bg-white px-4 py-3">
            <div className="mb-1 flex items-center justify-between text-xs text-secondary-500">
              <span>Progresso das atividades</span>
              <span className="font-semibold text-secondary-800">{activities.progressPercent}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-secondary-100">
              <div
                className="h-full rounded-full bg-emerald-500"
                style={{ width: `${activities.progressPercent}%` }}
              />
            </div>
          </div>
          <label className="inline-flex items-center gap-2 rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-700">
            <span className="sr-only">Etapa</span>
            <select
              value={stageFilter}
              onChange={(event) => setStageFilter(event.target.value)}
              className="border-0 bg-transparent p-0 text-sm focus:ring-0"
            >
              <option value="all">Todas as etapas</option>
              {activities.stages.map((stage) => (
                <option key={stage.id} value={stage.id}>
                  {stage.label}
                </option>
              ))}
            </select>
            <ChevronDownIcon className="h-4 w-4 text-secondary-400" aria-hidden />
          </label>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {viewOptions.map((option) => {
            const Icon = option.icon;
            const active = view === option.id;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => setView(option.id)}
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                  active
                    ? 'bg-primary-500 text-white'
                    : 'border border-secondary-200 bg-white text-secondary-700 hover:bg-secondary-50'
                }`}
              >
                <Icon className="h-4 w-4" aria-hidden />
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[14rem] flex-1">
          <MagnifyingGlassIcon
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400"
            aria-hidden
          />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar atividade..."
            className="w-full rounded-lg border border-secondary-200 bg-white py-2 pl-9 pr-3 text-sm text-secondary-800 placeholder:text-secondary-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value as 'all' | PortalActivityStatus)}
          className="rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-700"
        >
          <option value="all">Status</option>
          <option value="todo">A fazer</option>
          <option value="inProgress">Em andamento</option>
          <option value="review">Em revisão</option>
          <option value="done">Concluído</option>
        </select>
        <select
          value={ownerFilter}
          onChange={(event) => setOwnerFilter(event.target.value)}
          className="rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-700"
        >
          <option value="all">Responsável</option>
          {owners.map((owner) => (
            <option key={owner} value={owner}>
              {owner}
            </option>
          ))}
        </select>
        <select
          value={stageFilter}
          onChange={(event) => setStageFilter(event.target.value)}
          className="rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-700"
        >
          <option value="all">Etapa</option>
          {activities.stages.map((stage) => (
            <option key={stage.id} value={stage.id}>
              {stage.label}
            </option>
          ))}
        </select>
        <select
          value={priorityFilter}
          onChange={(event) => setPriorityFilter(event.target.value as 'all' | PortalActivityPriority)}
          className="rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-700"
        >
          <option value="all">Prioridade</option>
          <option value="high">Alta</option>
          <option value="medium">Média</option>
          <option value="low">Baixa</option>
        </select>
        {hasFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className="rounded-none px-2 py-2 text-sm font-semibold text-primary-700 hover:underline"
          >
            Limpar filtros
          </button>
        ) : null}
        <button type="button" className="erp-btn-primary ml-auto inline-flex items-center gap-1.5">
          <PlusIcon className="h-4 w-4" aria-hidden />
          Nova atividade
        </button>
      </div>

      {view === 'board' ? (
        <KanbanBoard
          items={filtered}
          selectedId={selected?.id}
          onSelect={(itemId) => {
            setSelectedId(itemId);
            setDetailTab('details');
          }}
        />
      ) : null}
      {view === 'list' ? (
        <ActivitiesList
          items={filtered}
          selectedId={selected?.id}
          onSelect={(itemId) => {
            setSelectedId(itemId);
            setDetailTab('details');
          }}
        />
      ) : null}
      {view === 'backlog' ? (
        <BacklogList
          items={filtered.filter((item) => item.inBacklog || item.status === 'todo')}
          selectedId={selected?.id}
          onSelect={(itemId) => {
            setSelectedId(itemId);
            setDetailTab('details');
          }}
        />
      ) : null}

      {selected ? (
        <ActivityDetailDrawer
          item={selected}
          activeTab={detailTab}
          onTabChange={setDetailTab}
          onClose={() => setSelectedId(null)}
          projectPath={projectPath}
        />
      ) : null}
    </div>
  );
}

function KanbanBoard({
  items,
  selectedId,
  onSelect,
}: {
  items: PortalActivityItem[];
  selectedId?: string;
  onSelect: (itemId: string) => void;
}) {
  return (
    <div className="flex gap-4 overflow-x-auto pb-2">
      {boardColumns.map((column) => {
        const columnItems = items.filter((item) => item.status === column.id);
        return (
          <section
            key={column.id}
            className="flex w-72 shrink-0 flex-col rounded-2xl border border-secondary-200 bg-secondary-50/60"
          >
            <header className={`flex items-center justify-between rounded-t-2xl px-3 py-2.5 ${column.headerClass}`}>
              <h3 className="text-sm font-semibold">{column.label}</h3>
              <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${column.badgeClass}`}>
                {columnItems.length}
              </span>
            </header>
            <ul className="flex flex-1 flex-col gap-2 p-2">
              {columnItems.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(item.id)}
                    className={`w-full rounded-xl border bg-white p-3 text-left shadow-sm transition-colors ${
                      selectedId === item.id
                        ? 'border-primary-400 ring-2 ring-primary-200'
                        : 'border-secondary-200 hover:border-primary-200'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-secondary-900">{item.title}</p>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${priorityClass[item.priority]}`}>
                        {priorityLabel[item.priority]}
                      </span>
                    </div>
                    <p className="mt-1 line-clamp-2 text-xs text-secondary-500">{item.description}</p>
                    <div className="mt-3 flex items-center justify-between gap-2 text-xs text-secondary-500">
                      <span className="inline-flex items-center gap-1">
                        <CalendarDaysIcon className="h-3.5 w-3.5" aria-hidden />
                        {item.dueLabel}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <ChatBubbleLeftIcon className="h-3.5 w-3.5" aria-hidden />
                        {item.commentCount}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-50 text-[10px] font-semibold text-primary-700">
                        {initials(item.ownerName)}
                      </span>
                      <span className="truncate text-xs font-medium text-secondary-700">{item.ownerName}</span>
                    </div>
                  </button>
                </li>
              ))}
              {columnItems.length === 0 ? (
                <li className="rounded-xl border border-dashed border-secondary-200 bg-white/70 px-3 py-6 text-center text-xs text-secondary-500">
                  Nenhuma atividade
                </li>
              ) : null}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function ActivitiesList({
  items,
  selectedId,
  onSelect,
}: {
  items: PortalActivityItem[];
  selectedId?: string;
  onSelect: (itemId: string) => void;
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-secondary-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-secondary-200 bg-secondary-50 text-xs font-medium text-secondary-500">
            <tr>
              <th className="px-4 py-3 font-medium">Atividade</th>
              <th className="px-3 py-3 font-medium">Status</th>
              <th className="px-3 py-3 font-medium">Prioridade</th>
              <th className="px-3 py-3 font-medium">Responsável</th>
              <th className="px-3 py-3 font-medium">Etapa</th>
              <th className="px-3 py-3 font-medium">Prazo</th>
              <th className="px-3 py-3 font-medium">Progresso</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr
                key={item.id}
                className={`cursor-pointer border-t border-secondary-100 ${
                  selectedId === item.id ? 'bg-primary-50/60' : 'hover:bg-secondary-50'
                }`}
                onClick={() => onSelect(item.id)}
              >
                <td className="px-4 py-3">
                  <p className="font-semibold text-secondary-900">{item.title}</p>
                  <p className="text-xs text-secondary-500">{item.reference}</p>
                </td>
                <td className="px-3 py-3">
                  <span className="inline-flex items-center gap-1.5 text-secondary-700">
                    <span className={`h-2 w-2 rounded-full ${statusDotClass[item.status]}`} />
                    {statusLabel[item.status]}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${priorityClass[item.priority]}`}>
                    {priorityLabel[item.priority]}
                  </span>
                </td>
                <td className="px-3 py-3 text-secondary-700">{item.ownerName}</td>
                <td className="px-3 py-3 text-secondary-600">{item.stageLabel}</td>
                <td className="whitespace-nowrap px-3 py-3 text-secondary-600">{item.dueLabel}</td>
                <td className="px-3 py-3">
                  <div className="min-w-[5rem]">
                    <div className="mb-1 text-xs font-semibold text-secondary-700">{item.progressPercent}%</div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-secondary-100">
                      <div
                        className="h-full rounded-full bg-primary-500"
                        style={{ width: `${item.progressPercent}%` }}
                      />
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-secondary-500">Nenhuma atividade com esse filtro.</p>
        ) : null}
      </div>
    </div>
  );
}

function BacklogList({
  items,
  selectedId,
  onSelect,
}: {
  items: PortalActivityItem[];
  selectedId?: string;
  onSelect: (itemId: string) => void;
}) {
  const ordered = items.slice().sort((left, right) => left.backlogOrder - right.backlogOrder);

  return (
    <div className="overflow-hidden rounded-2xl border border-secondary-200 bg-white shadow-sm">
      <div className="border-b border-secondary-100 px-5 py-4">
        <h3 className="text-base font-semibold text-secondary-900">Backlog priorizado</h3>
        <p className="mt-1 text-sm text-secondary-500">
          Backlog é a lista de funcionalidades, melhorias e atividades previstas para o projeto.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full table-fixed text-left text-sm">
          <colgroup>
            <col className="w-14" />
            <col className="w-[18%]" />
            <col className="w-[22%]" />
            <col className="w-28" />
            <col className="w-36" />
            <col className="w-[16%]" />
            <col className="w-28" />
            <col className="w-28" />
          </colgroup>
          <thead className="border-b border-secondary-200 bg-secondary-50 text-xs font-medium text-secondary-500">
            <tr>
              <th className="px-3 py-3 text-center font-medium">#</th>
              <th className="px-3 py-3 font-medium">Atividade</th>
              <th className="px-3 py-3 font-medium">Descrição</th>
              <th className="px-3 py-3 font-medium">Prioridade</th>
              <th className="px-3 py-3 font-medium">Responsável</th>
              <th className="px-3 py-3 font-medium">Etapa</th>
              <th className="px-3 py-3 font-medium">Status</th>
              <th className="px-3 py-3 font-medium">Situação</th>
            </tr>
          </thead>
          <tbody>
            {ordered.map((item, index) => (
              <tr
                key={item.id}
                className={`cursor-pointer border-t border-secondary-100 ${
                  selectedId === item.id ? 'bg-primary-50/60' : 'hover:bg-secondary-50'
                }`}
                onClick={() => onSelect(item.id)}
              >
                <td className="px-3 py-3 text-center">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-secondary-100 text-xs font-bold text-secondary-600">
                    {index + 1}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <p className="truncate font-semibold text-secondary-900" title={item.title}>
                    {item.title}
                  </p>
                  <p className="truncate text-xs text-secondary-500">{item.reference}</p>
                </td>
                <td className="px-3 py-3 text-secondary-600">
                  <p className="truncate" title={item.description}>
                    {item.description}
                  </p>
                </td>
                <td className="px-3 py-3">
                  <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${priorityClass[item.priority]}`}>
                    {priorityLabel[item.priority]}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <p className="truncate text-secondary-700" title={item.ownerName}>
                    {item.ownerName}
                  </p>
                </td>
                <td className="px-3 py-3">
                  <p className="truncate text-secondary-600" title={item.stageLabel}>
                    {item.stageLabel}
                  </p>
                </td>
                <td className="px-3 py-3">
                  <span className="inline-flex items-center gap-1.5 truncate text-secondary-700">
                    <span className={`h-2 w-2 shrink-0 rounded-full ${statusDotClass[item.status]}`} />
                    <span className="truncate">{statusLabel[item.status]}</span>
                  </span>
                </td>
                <td className="px-3 py-3">
                  {item.inBacklog ? (
                    <span className="inline-flex rounded-full bg-violet-50 px-2.5 py-0.5 text-xs font-semibold text-violet-700">
                      No backlog
                    </span>
                  ) : (
                    <span className="text-xs text-secondary-400">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {ordered.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-secondary-500">Nenhum item no backlog com esse filtro.</p>
        ) : null}
      </div>
    </div>
  );
}

function ActivityDetailDrawer({
  item,
  activeTab,
  onTabChange,
  onClose,
  projectPath,
}: {
  item: PortalActivityItem;
  activeTab: DetailTab;
  onTabChange: (tab: DetailTab) => void;
  onClose: () => void;
  projectPath: string;
}) {
  const checklistDone = item.checklist.filter((entry) => entry.done).length;

  return (
    <DraggableDetailPanel
      resetKey={item.id}
      header={
        <div className="flex items-start justify-between gap-3 border-b border-secondary-100 px-4 py-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold text-secondary-900">{item.title}</h3>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${priorityClass[item.priority]}`}>
                {priorityLabel[item.priority]}
              </span>
            </div>
            <p className="mt-1 text-xs text-secondary-500">{item.reference}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-secondary-400 hover:bg-secondary-50 hover:text-secondary-700"
            aria-label="Fechar detalhes"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden />
          </button>
        </div>
      }
    >
      <div className="border-b border-secondary-200 px-2">
        <nav className="-mb-px flex gap-1" aria-label="Detalhe da atividade">
          {(
            [
              ['details', 'Detalhes'],
              ['comments', `Comentários (${item.comments.length || item.commentCount})`],
              ['files', `Arquivos (${item.files.length})`],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => onTabChange(id)}
              className={`rounded-none border-b-2 px-3 py-2.5 text-sm font-medium ${
                activeTab === id
                  ? 'border-primary-500 text-primary-700'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {activeTab === 'details' ? (
          <div className="space-y-4">
            <section>
              <p className="text-xs font-medium text-secondary-500">Descrição</p>
              <p className="mt-1 text-sm leading-relaxed text-secondary-700">{item.description}</p>
            </section>
            <section className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary-700">
                {initials(item.ownerName)}
              </span>
              <div>
                <p className="text-xs font-medium text-secondary-500">Responsável</p>
                <p className="text-sm font-semibold text-secondary-900">{item.ownerName}</p>
                <p className="text-xs text-secondary-500">{item.ownerRole}</p>
              </div>
            </section>
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <DetailField label="Etapa" value={item.stageLabel} />
              <DetailField
                label="Status"
                value={
                  <span className="inline-flex items-center gap-1.5">
                    <span className={`h-2 w-2 rounded-full ${statusDotClass[item.status]}`} />
                    {statusLabel[item.status]}
                  </span>
                }
              />
              <DetailField
                label="Prioridade"
                value={
                  <span className="inline-flex items-center gap-1.5">
                    <span
                      className={`h-2 w-2 rounded-full ${
                        item.priority === 'high'
                          ? 'bg-red-500'
                          : item.priority === 'medium'
                            ? 'bg-secondary-400'
                            : 'bg-emerald-500'
                      }`}
                    />
                    {priorityLabel[item.priority]}
                  </span>
                }
              />
              <DetailField label="Início" value={item.startDate} />
              <DetailField label="Término previsto" value={item.endDate} />
            </dl>
            <section>
              <div className="mb-1 flex items-center justify-between text-xs text-secondary-500">
                <span>Progresso</span>
                <span className="font-semibold text-secondary-800">{item.progressPercent}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-secondary-100">
                <div className="h-full rounded-full bg-primary-500" style={{ width: `${item.progressPercent}%` }} />
              </div>
            </section>
            {item.checklist.length > 0 ? (
              <section>
                <p className="text-sm font-semibold text-secondary-900">
                  Checklist ({checklistDone}/{item.checklist.length})
                </p>
                <ul className="mt-2 space-y-2">
                  {item.checklist.map((entry) => (
                    <li key={entry.id} className="flex items-start gap-2 text-sm text-secondary-700">
                      <span
                        className={`mt-0.5 flex h-4 w-4 items-center justify-center rounded border ${
                          entry.done
                            ? 'border-primary-500 bg-primary-500 text-white'
                            : 'border-secondary-300 bg-white'
                        }`}
                      >
                        {entry.done ? <CheckIcon className="h-3 w-3" aria-hidden /> : null}
                      </span>
                      <span className={entry.done ? 'text-secondary-500 line-through' : ''}>{entry.label}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
            {item.dependencyLabel ? (
              <section>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-secondary-900">Dependências</p>
                  <Link
                    to={`${projectPath}?tab=planning`}
                    className="text-xs font-semibold text-primary-700 hover:underline"
                  >
                    Ver no Gantt
                  </Link>
                </div>
                <div className="mt-2 flex items-center gap-2 rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-2 text-sm text-secondary-700">
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white">
                    <CheckIcon className="h-3 w-3" aria-hidden />
                  </span>
                  {item.dependencyLabel}
                </div>
              </section>
            ) : null}
          </div>
        ) : null}

        {activeTab === 'comments' ? (
          <ul className="space-y-4">
            {item.comments.length === 0 ? (
              <li className="text-sm text-secondary-500">Nenhum comentário nesta atividade.</li>
            ) : (
              item.comments.map((comment) => (
                <li key={comment.id} className="flex gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50 text-[10px] font-semibold text-primary-700">
                    {initials(comment.authorName)}
                  </span>
                  <div>
                    <p className="text-sm text-secondary-800">
                      <span className="font-semibold">{comment.authorName}</span>
                      <span className="text-secondary-400"> · {comment.whenLabel}</span>
                    </p>
                    <p className="mt-1 text-sm text-secondary-700">{comment.body}</p>
                  </div>
                </li>
              ))
            )}
          </ul>
        ) : null}

        {activeTab === 'files' ? (
          <ul className="space-y-2">
            {item.files.length === 0 ? (
              <li className="text-sm text-secondary-500">Nenhum arquivo anexado.</li>
            ) : (
              item.files.map((file) => (
                <li
                  key={file.id}
                  className="flex items-center gap-3 rounded-xl border border-secondary-200 bg-secondary-50 px-3 py-2.5"
                >
                  <DocumentTextIcon className="h-5 w-5 text-secondary-500" aria-hidden />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-secondary-900">{file.name}</p>
                    <p className="text-xs text-secondary-500">{file.sizeLabel}</p>
                  </div>
                </li>
              ))
            )}
          </ul>
        ) : null}
      </div>
    </DraggableDetailPanel>
  );
}

function DetailField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium text-secondary-500">{label}</dt>
      <dd className="mt-0.5 font-medium text-secondary-900">{value}</dd>
    </div>
  );
}
